import {
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutline,
  Close,
  HourglassEmpty,
  NotificationsActive,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useState } from 'react';
import type { UserRecord } from './adminTypes';
import { ROLE_OPTIONS } from '../../constants/roles';

interface PendingApprovalsBannerProps {
  users: UserRecord[];
  onApprove: (user: UserRecord, role: string) => void;
  onReject: (user: UserRecord) => void;
}

const formatRelative = (dateStr: string): string => {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

export const PendingApprovalsBanner = ({ users, onApprove, onReject }: PendingApprovalsBannerProps) => {
  const [selectedRoles, setSelectedRoles] = useState<Record<string, string>>({});

  return (
    <Paper elevation={0} sx={{
      mb: 3,
      border: `1px solid ${alpha('#FFC107', 0.4)}`,
      borderRadius: '12px',
      overflow: 'hidden',
      backgroundColor: alpha('#FFF8E1', 0.5),
    }}>
      {/* Header */}
      <Box sx={{
        px: 2.5, py: 1.5,
        display: 'flex', alignItems: 'center', gap: 1,
        borderBottom: `1px solid ${alpha('#FFC107', 0.25)}`,
        backgroundColor: alpha('#FFC107', 0.08),
      }}>
        <NotificationsActive sx={{ fontSize: 18, color: '#F57F17' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#F57F17', fontSize: '0.8125rem' }}>
          Pending Approvals
        </Typography>
        <Chip label={users.length} size="small" sx={{
          height: 20, fontSize: '0.7rem', fontWeight: 700,
          backgroundColor: alpha('#F57F17', 0.15),
          color: '#F57F17',
        }} />
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          Users who signed in but need access approval
        </Typography>
      </Box>

      {/* Pending user rows */}
      {users.map((user) => {
        const role = selectedRoles[user.AppUserKey] || 'reader';
        return (
          <Collapse in key={user.AppUserKey}>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 2,
              px: 2.5, py: 1.5,
              borderBottom: `1px solid ${alpha('#FFC107', 0.15)}`,
              '&:last-child': { borderBottom: 'none' },
            }}>
              <Avatar sx={{
                width: 36, height: 36, fontSize: '0.85rem', fontWeight: 600,
                bgcolor: alpha('#F57F17', 0.12),
                color: '#F57F17',
              }}>
                <HourglassEmpty sx={{ fontSize: 18 }} />
              </Avatar>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8125rem' }} noWrap>
                  {user.DisplayName || user.Email}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {user.Email} &middot; Requested {formatRelative(user.sysCreateDate)}
                </Typography>
              </Box>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={role}
                  onChange={(e) => setSelectedRoles((prev) => ({ ...prev, [user.AppUserKey]: e.target.value }))}
                  sx={{ fontSize: '0.8125rem', '& .MuiSelect-select': { py: 0.5 } }}
                >
                  {ROLE_OPTIONS.map((r) => (
                    <MenuItem key={r.value} value={r.value} sx={{ fontSize: '0.8125rem' }}>
                      {r.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckCircleOutline sx={{ fontSize: 16 }} />}
                onClick={() => onApprove(user, role)}
                sx={{
                  textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem',
                  borderRadius: '6px', px: 2,
                }}
              >
                Approve
              </Button>

              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<Close sx={{ fontSize: 14 }} />}
                onClick={() => onReject(user)}
                sx={{
                  textTransform: 'none', fontSize: '0.75rem',
                  borderRadius: '6px', minWidth: 'auto', px: 1.5,
                }}
              >
                Reject
              </Button>
            </Box>
          </Collapse>
        );
      })}
    </Paper>
  );
};
